import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { GOOGLE_MAPS_MAP_ID } from '~/constants';
import { LibraryStatus, useGoogleMapsStore } from '~/stores/GoogleMaps';
import { useNotificationStore } from '~/stores/Notification';
import { ApiAddress } from '~/typings/API';

type MapProps = {
  height?: string;
  width?: string;
  margin?: string;
  /**
   * Render nothing until the map has drawn, then fade it in, instead of showing a loading / unavailable
   * placeholder. Fades back out if a later lookup fails.
   */
  fadeIn?: boolean;
  /** Show zoom in / out buttons. */
  controls?: boolean;
} & ({ address: ApiAddress; searchQuery?: never } | { address?: never; searchQuery: string });

const MapContainer = styled.div<{ height: string; width: string; margin: string; $hidden?: boolean }>`
  position: relative;
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  border-radius: 8px;
  overflow: hidden;
  margin: ${({ margin }) => margin};
  opacity: ${({ $hidden }) => ($hidden ? 0 : 1)};
  transition: opacity 300ms ease-out;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.075);
    pointer-events: none;
    z-index: 1;
  }

  .gm-style-cc,
  .gm-style a[href^='https://maps.google.com/maps'],
  .gm-style a[href^='https://www.google.com/maps'],
  .gm-style-cc div,
  .gm-style-mtc {
    display: none !important;
  }
`;

const MapInner = styled.div`
  width: 100%;
  height: 100%;
`;

const LoadingPlaceholder = styled.div<{ background: string }>`
  width: 100%;
  height: 100%;
  background: ${({ background }) => background};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const formatAddressForGeocoding = (address: ApiAddress): string => {
  return [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postalCode,
    address.countryCode === 'US' ? 'USA' : address.countryCode,
  ]
    .filter(Boolean)
    .join(' ');
};

const darkenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.floor(num / 65536);
  const g = Math.floor((num % 65536) / 256);
  const b = num % 256;
  const darkenedR = Math.max(0, Math.floor(r * (1 - percent)));
  const darkenedG = Math.max(0, Math.floor(g * (1 - percent)));
  const darkenedB = Math.max(0, Math.floor(b * (1 - percent)));
  const result = darkenedR * 65536 + darkenedG * 256 + darkenedB;
  return `#${result.toString(16).padStart(6, '0')}`;
};

const buildMarkerContent = (primaryColor: string): HTMLElement => {
  const darkerColor = darkenColor(primaryColor, 0.5);
  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M16 0C7.163 0 0 7.163 0 16C0 24.837 16 40 16 40C16 40 32 24.837 32 16C32 7.163 24.837 0 16 0Z" fill="${primaryColor}"/>
      <circle cx="16" cy="16" r="6" fill="${darkerColor}"/>
    </svg>
  `;
  return wrapper;
};

export const Map = ({
  address,
  searchQuery,
  height = '132px',
  width = '400px',
  margin = '0',
  fadeIn,
  controls,
}: MapProps) => {
  const theme = useTheme();
  const addDangerNotifcation = useNotificationStore((s) => s.addDangerNotification);
  const locationLibrary = searchQuery ? 'places' : 'geocoding';
  const { loadLibrary, isMapsLoaded, hasLoadError } = useGoogleMapsStore(
    useShallow((s) => ({
      loadLibrary: s.loadLibrary,
      isMapsLoaded:
        s.libraryStatus.maps === LibraryStatus.Loaded &&
        s.libraryStatus.marker === LibraryStatus.Loaded &&
        s.libraryStatus[locationLibrary] === LibraryStatus.Loaded,
      hasLoadError:
        s.libraryStatus.maps === LibraryStatus.Error ||
        s.libraryStatus.marker === LibraryStatus.Error ||
        s.libraryStatus[locationLibrary] === LibraryStatus.Error,
    }))
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  const [center, setCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(true);
  const [tilesLoaded, setTilesLoaded] = useState(false);

  useEffect(() => {
    loadLibrary(['maps', 'marker', locationLibrary]).catch(() => {
      addDangerNotifcation({ content: 'Failed to load maps' });
    });
  }, [loadLibrary, addDangerNotifcation, locationLibrary]);

  useEffect(() => {
    if (!isMapsLoaded) {
      return;
    }

    if (!searchQuery && (!address?.line1 || !address.city)) {
      setCenter(null);
      setIsGeocoding(false);
      return;
    }

    let cancelled = false;
    setIsGeocoding(true);
    // Async so anything the lookups throw lands in the catch below: once Maps rejects the API key (e.g. for a
    // referrer it doesn't allow), geocode() returns undefined instead of a promise.
    const lookup = async () => {
      if (searchQuery) {
        const { places } = await google.maps.places.Place.searchByText({
          textQuery: searchQuery,
          fields: ['location'],
          maxResultCount: 1,
        });
        return places[0]?.location;
      }
      const response = await new google.maps.Geocoder().geocode({ address: formatAddressForGeocoding(address!) });
      return response?.results[0]?.geometry.location;
    };

    lookup()
      .then((location) => {
        if (!cancelled) {
          setCenter(location ? { lat: location.lat(), lng: location.lng() } : null);
        }
      })
      .catch(() => {
        if (!cancelled) setCenter(null);
      })
      .finally(() => {
        if (!cancelled) setIsGeocoding(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isMapsLoaded, address, searchQuery]);

  const isDark = theme.id === 'dark';
  const placeholderBackground = isDark ? '#15171b' : '#efebe2';

  const markerContent = useMemo(() => buildMarkerContent(theme.primary.background), [theme.primary.background]);

  useEffect(() => {
    const container = containerRef.current;
    if (!isMapsLoaded || !center || !container) return;

    // The container remounts when the placeholder swaps out or the theme changes; anything else just moves the map.
    if (mapRef.current?.getDiv() !== container) {
      setTilesLoaded(false);
      mapRef.current = new google.maps.Map(container, {
        center,
        zoom: 14,
        mapId: GOOGLE_MAPS_MAP_ID,
        colorScheme: isDark ? google.maps.ColorScheme.DARK : google.maps.ColorScheme.LIGHT,
        disableDefaultUI: true,
        zoomControl: false,
        streetViewControl: false,
        mapTypeControl: false,
        fullscreenControl: false,
        draggableCursor: 'grab',
        draggingCursor: 'grabbing',
      });
      mapRef.current.addListener('tilesloaded', () => setTilesLoaded(true));
    } else {
      mapRef.current.panTo(center);
    }

    if (markerRef.current) {
      markerRef.current.map = null;
    }
    markerRef.current = new google.maps.marker.AdvancedMarkerElement({
      map: mapRef.current,
      position: center,
      content: markerContent,
    });
  }, [isMapsLoaded, center, markerContent, isDark]);

  useEffect(
    () => () => {
      if (markerRef.current) markerRef.current.map = null;
    },
    []
  );

  // Google's own zoom control can be resized but not restyled, so these stand in for it.
  const zoomControls = controls && (
    <div className="absolute right-2 bottom-2 z-2 flex flex-col overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-black/10 dark:bg-gray-900 dark:ring-white/15">
      {[
        { label: 'Zoom in', delta: 1, path: 'M7 2.5v9M2.5 7h9' },
        { label: 'Zoom out', delta: -1, path: 'M2.5 7h9' },
      ].map(({ label, delta, path }) => (
        <button
          key={label}
          type="button"
          aria-label={label}
          onClick={() => mapRef.current?.setZoom((mapRef.current.getZoom() ?? 14) + delta)}
          className="flex size-7 cursor-pointer items-center justify-center text-gray-500 transition-colors not-first:border-t not-first:border-black/10 hover:bg-gray-50 hover:text-foreground dark:text-gray-400 dark:not-first:border-white/15 dark:hover:bg-white/10"
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
            <path d={path} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      ))}
    </div>
  );

  if (fadeIn) {
    // Mounted (but transparent) until there's a center, so a failed lookup fades out rather than unmounting the map.
    if (!isMapsLoaded) return null;
    return (
      <MapContainer height={height} width={width} margin={margin} $hidden={!center || !tilesLoaded}>
        <MapInner key={theme.id} ref={containerRef} />
        {zoomControls}
      </MapContainer>
    );
  }

  if (!isMapsLoaded || !center) {
    return (
      <MapContainer height={height} width={width} margin={margin}>
        <LoadingPlaceholder background={placeholderBackground} role="status">
          {hasLoadError || (isMapsLoaded && !isGeocoding) ? 'Location unavailable' : 'Loading map…'}
        </LoadingPlaceholder>
      </MapContainer>
    );
  }

  return (
    <MapContainer height={height} width={width} margin={margin}>
      <MapInner key={theme.id} ref={containerRef} />
      {zoomControls}
    </MapContainer>
  );
};
